import { Head, Link } from '@inertiajs/react';
import PublicLayout from '@/layouts/public-layout';
import { BookOpen } from 'lucide-react';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';
import { Genre } from '@/types/models';
import { useTranslations } from '@/hooks/use-translations';
import { SeoHead } from '@/components/seo-head';

interface GenresPageProps {
    genres: Genre[];
    locale: string;
}

export default function Genres({ genres, locale }: GenresPageProps) {
    const { genreUrl } = useTranslatedRoutes();
    const { t } = useTranslations();

    // Get the base URL from environment
    const baseUrl = import.meta.env.VITE_APP_URL || 'http://localhost:8000';
    
    // Generate structured data for collection page
    const collectionJsonLd = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": t('All Genres'),
        "description": t('Browse our collection of books organized by literary genres'),
        "numberOfItems": genres.length,
        "hasPart": genres.map(genre => ({
            "@type": "Collection",
            "name": genre.name,
            "url": `${baseUrl}${genreUrl((genre as any).translated_slug || genre.slug)}`,
            "numberOfItems": genre.books_count || 0
        }))
    };

    return (
        <PublicLayout>
            <SeoHead
                title={t('All Genres')}
                description={t('Browse our collection of books organized by literary genres. Find your favorite genre from our extensive library.')}
                jsonLd={collectionJsonLd}
            />

            {/* Header Section */}
            <section className="py-6 md:py-8">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold mb-2">
                            {t('Browse by Genre')}
                        </h1>
                        <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-8">
                            {t('Explore our collection organized by literary genres')}
                        </p>
                        <div className="max-w-4xl mx-auto">
                            <hr className="border-t border-border" />
                        </div>
                    </div>

                    {/* Genres List */}
                    {genres && genres.length > 0 ? (
                        <div className="max-w-4xl mx-auto">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-8 gap-y-1">
                                {genres.map((genre) => (
                                    <Link
                                        key={genre.id}
                                        href={genreUrl((genre as any).translated_slug || genre.slug)}
                                        className="group flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors"
                                    >
                                        <span className="text-sm font-medium group-hover:text-primary transition-colors">
                                            {genre.name}
                                        </span>
                                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                            {genre.books_count || 0}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <BookOpen className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                            <h2 className="text-2xl font-semibold mb-2">{t('No genres found')}</h2>
                            <p className="text-muted-foreground">
                                {t('No genres have been added to the library yet.')}
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}